import { dsClass } from '../../common/constants/dsClasses.js';
import {
  billingAccountExtensionNameMsg,
  billingAccountNameMsg,
  payerDetailsMsg,
  payerMsg,
  referenceMsg,
  streetAddressMsg,
  t,
} from '../../common/i18n/index.js';
import { FORM_GRID_CLEAR, FormGridFieldset } from '../../common/react-hook-form/FormGridFieldset/FormGridFieldset.js';
import { Contact } from '../../common/react-hook-form/fields/Contact';
import { InvoiceType } from '../../common/react-hook-form/fields/InvoiceType.js';
import { PostOffice } from '../../common/react-hook-form/fields/PostOffice.js';
import { Reference } from '../../common/react-hook-form/fields/Reference.js';
import { StreetAddress } from '../../common/react-hook-form/fields/StreetAddress.js';
import { Name, PostalCode, TextInput } from '../../common/react-hook-form/fields/index.js';
import { validateBillingAccountReferenceMaxLength } from '../../common/utils/billingAccountUtils';
import {
  BILLING_ACCOUNT_MAX_LENGTH,
  BILLING_ACCOUNT_NAME_EXT_MAX_LENGTH,
  BILLING_ACCOUNT_PAYER_NAME_EXTENSION_MAX_LENGTH,
  BILLING_ACCOUNT_REFERENCE_TEXT_MAX_LENGTH,
} from '../../common/utils/validationUtils.js';
import { BillingAccount } from '../../generated/api/billingAccount.js';
import type { BillChannel } from '../../generated/api/billChannel.js';
import type { CompanyInfoResponse } from '../../generated/api/companyInfoResponse.js';
import type { Contact as ContactType } from '../../generated/api/contact';

export const billingAccountDefaultValues = (companyInfo?: CompanyInfoResponse) => ({
  payerName: companyInfo?.companyName || '',
  payerNameExtension: '',
  payerAddress: {
    countryCode: 'FIN',
    line1: companyInfo?.address?.line1 || '',
    postOffice: companyInfo?.address?.postOffice || '',
    postalCode: companyInfo?.address?.postalCode || '',
  },
  billingContactId: '',
  billFormatType: BillingAccount.BillFormatTypeEnum.CONSOLIDATED_INVOICE_WITH_ITEMIZATION,
  billElectronicOperator: undefined,
  billingAccountName: companyInfo?.companyName || '',
});

interface BillingAccountFieldSetProps {
  billChannels: BillChannel[];
  contacts: ContactType[];
  onCreateBillingContact: () => void;
}

export const BillingAccountFieldset = (props: BillingAccountFieldSetProps) => (
  <FormGridFieldset>
    <Name
      name="billingAccount.payerName"
      label={t.PB6S(payerMsg)}
      placeholder=""
      allowSpecialCharacters={true}
      disabled={true}
    />
    <Name
      name="billingAccount.payerNameExtension"
      label={t.YLAI(payerDetailsMsg)}
      placeholder=""
      allowSpecialCharacters={true}
      maxLength={BILLING_ACCOUNT_PAYER_NAME_EXTENSION_MAX_LENGTH}
      required={false}
    />
    <StreetAddress name="billingAccount.payerAddress.line1" label={t.DD38(streetAddressMsg)} />
    <PostalCode name="billingAccount.payerAddress.postalCode" placeholder="" />
    <PostOffice name="billingAccount.payerAddress.postOffice" wrapperClassName={dsClass.PADDING_BOTTOM_6} />
    <TextInput type="hidden" name="billingAccount.payerAddress.countryCode" />
    <Contact
      className={FORM_GRID_CLEAR}
      contacts={props.contacts || []}
      name="billingAccount.billingContactId"
      reference="contact"
      onCreateContact={props.onCreateBillingContact}
    />
    <InvoiceType name="billingAccount.billFormatType" />
    <Name
      name="billingAccount.billingAccountName"
      maxLength={BILLING_ACCOUNT_MAX_LENGTH}
      label={t.RH6T(billingAccountNameMsg)}
      placeholder=""
      required={true}
      allowSpecialCharacters={true}
    />
    <Name
      label={t.KUTS(billingAccountExtensionNameMsg)}
      maxLength={BILLING_ACCOUNT_NAME_EXT_MAX_LENGTH}
      name="billingAccount.billingAccountExtensionName"
      placeholder=""
      required={false}
      allowSpecialCharacters={true}
      wrapperClassName={dsClass.PADDING_BOTTOM_6}
    />
    <Reference
      hint=""
      name="billingAccount.customerReference1"
      label={`${t.LVRN(referenceMsg)} 1`}
      maxLength={BILLING_ACCOUNT_REFERENCE_TEXT_MAX_LENGTH}
      validate={validateBillingAccountReferenceMaxLength}
    />
    <Reference
      hint=""
      name="billingAccount.customerReference2"
      label={`${t.LVRN(referenceMsg)} 2`}
      maxLength={BILLING_ACCOUNT_REFERENCE_TEXT_MAX_LENGTH}
      validate={validateBillingAccountReferenceMaxLength}
    />
    <Reference
      hint=""
      name="billingAccount.customerReference3"
      label={`${t.LVRN(referenceMsg)} 3`}
      maxLength={BILLING_ACCOUNT_REFERENCE_TEXT_MAX_LENGTH}
      validate={validateBillingAccountReferenceMaxLength}
    />
    <Reference
      hint=""
      label={`${t.LVRN(referenceMsg)} 4`}
      name="billingAccount.customerReference4"
      maxLength={BILLING_ACCOUNT_REFERENCE_TEXT_MAX_LENGTH}
      validate={validateBillingAccountReferenceMaxLength}
      wrapperClassName={dsClass.PADDING_BOTTOM_6}
    />
  </FormGridFieldset>
);
